/**
 * incomingCallSessionReducer — pure function du cycle de vie inbound.
 *
 * Extrait du hook useIncomingCallSession.tsx (2026-06-04) pour permettre
 * le test unitaire en isolation. Aucune dependance React, aucun side
 * effect — uniquement reducer pur.
 *
 * Voir incomingCallSessionReducer.test.ts pour les invariants verifies.
 */

export type Phase = 'idle' | 'ringing' | 'accepted' | 'live' | 'ended';

export interface ProspectInfo {
  name: string;
  phone: string;
  company: string;
  isKnown: boolean;
}

export interface Segment {
  speaker: string;
  text: string;
}

export interface IncomingCallState {
  phase: Phase;
  callSid: string | null;
  /** The parent call's sid: the key of its live transcript, coaching data and recording. */
  mediaCallSid: string | null;
  parallelCallId: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  twilioCall: any | null;
  prospect: ProspectInfo | null;
  startedAt: number | null;
  endedAt: number | null;
  lastSegment: Segment | null;
}

export const initialIncomingCallState: IncomingCallState = {
  phase: 'idle',
  callSid: null,
  mediaCallSid: null,
  parallelCallId: null,
  twilioCall: null,
  prospect: null,
  startedAt: null,
  endedAt: null,
  lastSegment: null,
};

export type IncomingCallAction =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | { type: 'INCOMING_RECEIVED'; twilioCall: any; callSid: string; mediaCallSid?: string; parallelCallId?: string; prospect: ProspectInfo }
  | { type: 'ACCEPT_START' }
  | { type: 'ACCEPT_SUCCESS'; parallelCallId: string }
  | { type: 'BRIDGED'; callSid?: string }
  | { type: 'REJECTED' }
  | { type: 'HANGUP'; reason: 'local' | 'remote' | 'cancel' | 'error'; callSid?: string }
  | { type: 'SEGMENT'; segment: Segment }
  | { type: 'RESET' };

/**
 * Reducer pur. Garanties :
 *  - Ne mute jamais state (toujours retourne un nouvel objet ou state intact)
 *  - Retourne state intact si l'action n'est pas applicable au phase courant
 *    (pas d'erreur, juste no-op)
 *  - Etat 'ended' est terminal (sauf RESET)
 */
export function incomingCallSessionReducer(
  state: IncomingCallState,
  action: IncomingCallAction,
): IncomingCallState {
  switch (action.type) {
    case 'INCOMING_RECEIVED':
      // Si on est deja sur un appel (live ou accepted), on ignore (1 appel a la fois)
      if (state.phase === 'live' || state.phase === 'accepted') return state;
      return {
        ...initialIncomingCallState,
        phase: 'ringing',
        twilioCall: action.twilioCall,
        callSid: action.callSid,
        mediaCallSid: action.mediaCallSid || action.callSid,
        // Server-created record (see /voice-handler): when present, ACCEPT
        // updates it in place instead of creating a competing second row.
        parallelCallId: action.parallelCallId || null,
        prospect: action.prospect,
      };
    case 'ACCEPT_START':
      if (state.phase !== 'ringing') return state;
      return { ...state, phase: 'accepted', startedAt: Date.now() };
    case 'ACCEPT_SUCCESS':
      if (state.phase !== 'accepted' && state.phase !== 'live') return state;
      return { ...state, parallelCallId: action.parallelCallId, phase: 'live' };
    case 'BRIDGED':
      // SDK events carry the leg they come from. With several devices of the
      // agent ringing (tabs, computers), each leg reports its own events; only
      // the leg this session follows may move it.
      if (action.callSid && state.callSid && action.callSid !== state.callSid) return state;
      // Pose par l'ecouteur 'accept' du SDK Twilio : le pont media est
      // ouvert. C'est la preuve que l'appel est decroche, donc qu'il ne
      // sonne plus, y compris quand ACCEPT_START n'a pas eu lieu (appel
      // externe decroche pendant que la creation du ParallelCall est encore
      // en vol). Sans 'ringing' ici, la session ne quittait jamais la
      // sonnerie et le bandeau d'appel entrant restait affiche.
      // Volontairement limite a ces deux phases : accepter BRIDGED depuis
      // 'idle' ressusciterait une session sans appel, et depuis 'ended'
      // rouvrirait un appel termine.
      if (state.phase !== 'accepted' && state.phase !== 'ringing') return state;
      return { ...state, phase: 'live', startedAt: state.startedAt ?? Date.now() };
    case 'REJECTED':
      if (state.phase !== 'ringing') return state;
      return { ...state, phase: 'ended', endedAt: Date.now() };
    case 'HANGUP':
      if (state.phase === 'idle' || state.phase === 'ended') return state;
      if (action.callSid && state.callSid && action.callSid !== state.callSid) return state;
      return { ...state, phase: 'ended', endedAt: Date.now() };
    case 'SEGMENT':
      if (state.phase !== 'live') return state;
      return { ...state, lastSegment: action.segment };
    case 'RESET':
      return initialIncomingCallState;
    default:
      return state;
  }
}
